/**
 * REST Countries API service
 * Documentation: https://restcountries.com/
 */

import axios from 'axios'

const BASE_URL = 'https://restcountries.com/v3.1'

/**
 * Fetch all countries with specific fields
 * @param {Array} fields - Optional fields to include in the response
 * @returns {Promise} - Promise containing country data
 */
export const fetchAllCountries = async (fields = ['name', 'flags', 'cca2', 'cca3', 'cioc']) => {
  try {
    const fieldsParam = fields.join(',')
    const response = await axios.get(`${BASE_URL}/all?fields=${fieldsParam}`)
    return response.data
  } catch (error) {
    console.error('Error fetching countries from REST Countries API:', error)
    return []
  }
}

/**
 * Get countries by specifying a region
 * @param {String} region - Region name (africa, americas, asia, europe, oceania)
 * @param {Array} fields - Optional fields to include in the response
 * @returns {Promise} - Promise containing filtered country data
 */
export const fetchCountriesByRegion = async (region, fields = ['name', 'flags', 'cca2']) => {
  try {
    const fieldsParam = fields.join(',')
    const response = await axios.get(`${BASE_URL}/region/${region}?fields=${fieldsParam}`)
    return response.data
  } catch (error) {
    console.error(`Error fetching countries by region ${region}:`, error)
    return []
  }
}

/**
 * Search countries by name
 * @param {String} name - Country name to search for
 * @param {Boolean} fullText - Whether to match the full name exactly
 * @param {Array} fields - Optional fields to include in the response
 * @returns {Promise} - Promise containing matching country data
 */
export const searchCountriesByName = async (
  name,
  fullText = false,
  fields = ['name', 'flags', 'cca2']
) => {
  try {
    const fieldsParam = fields.join(',')
    const fullTextParam = fullText ? '&fullText=true' : ''
    const response = await axios.get(
      `${BASE_URL}/name/${name}?fields=${fieldsParam}${fullTextParam}`
    )
    return response.data
  } catch (error) {
    console.error(`Error searching countries by name '${name}':`, error)
    return []
  }
}

/**
 * Fetch independent countries
 * @param {Boolean} status - Whether to fetch independent (true) or dependent (false) territories
 * @param {Array} fields - Optional fields to include in the response
 * @returns {Promise} - Promise containing filtered country data
 */
export const fetchIndependentCountries = async (
  status = true,
  fields = ['name', 'flags', 'cca2']
) => {
  try {
    const fieldsParam = fields.join(',')
    const response = await axios.get(
      `${BASE_URL}/independent?status=${status}&fields=${fieldsParam}`
    )
    return response.data
  } catch (error) {
    console.error(`Error fetching independent countries (status=${status}):`, error)
    return []
  }
}

/**
 * Format countries data from REST Countries API to be compatible with the application
 * @param {Array} countries - Countries from REST Countries API
 * @returns {Array} - Formatted country data for dropdown menus
 */
export const formatRestCountries = (countries) => {
  if (!countries || !Array.isArray(countries) || countries.length === 0) {
    return []
  }

  return countries
    .map((country, index) => {
      // Handle cases where country or required properties are missing
      if (!country || !country.name) {
        console.warn('Invalid country object encountered:', country)
        return null
      }

      // Use common name as the display name, with official name as fallback
      const countryName = country.name.common || country.name.official || `Country ${index}`

      // Use cca2 (ISO 3166-1 alpha-2) as the value, with fallbacks
      const countryCode = country.cca2 || country.cca3 || country.cioc || `country_${index}`

      return {
        key: countryCode,
        label: countryName,
        value: countryCode,
        flag: country.flags?.png || country.flags?.svg,
        // Include additional data that might be useful
        data: {
          cca2: country.cca2,
          cca3: country.cca3,
          cioc: country.cioc,
          officialName: country.name.official,
        },
      }
    })
    .filter(Boolean) // Remove any null entries
}

export default {
  fetchAllCountries,
  fetchCountriesByRegion,
  searchCountriesByName,
  fetchIndependentCountries,
  formatRestCountries,
}
